import Component from '@glimmer/component'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import ChartInfoTooltip from 'teacher-ui-client/components/giraffe/chart-info-tooltip'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import optional from 'teacher-ui-client/helpers/optional'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import { t, type IntlService } from 'ember-intl'
import { eq } from 'ember-truth-helpers'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import type Student from 'teacher-ui-client/models/student'
import type RouterService from '@ember/routing/router-service'
import { differenceWith } from 'lodash'
import type Owner from '@ember/owner'
import type { AwardsEarnedHistoryData } from 'teacher-ui-client/utils/student-events/fetchers/awards-earned-history'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface HistoryDataViewModel {
  studentId: string
  firstName: string
  lastName: string
  teacherName: string
  gradePosition: number
  gold: Nullable<number>
  silver: Nullable<number>
  bronze: Nullable<number>
  total: number
}

interface Args {
  data: AwardsEarnedHistoryData[]
  subco?: boolean
  uiScope: UIScopeName
  students: Student[]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class TrophiesHistory extends Component<Signature> {
  @service intl!: IntlService
  @service router!: RouterService

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<HistoryDataViewModel>

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get data() {
    return this.tableRows
  }

  get tableRows() {
    const stats = this.args.data

    return this.args.students.filter(hasId).map((student) => {
      const statRow = stats.find((stat) => String(stat.student_id) === student.id)

      const bronze = statRow?.bronze ?? null
      const silver = statRow?.silver ?? null
      const gold = statRow?.gold ?? null
      const total = (bronze ?? 0) + (silver ?? 0) + (gold ?? 0)

      return {
        firstName: student.firstName,
        lastName: student.lastName,
        teacherName: student.readingTeacherNames.join(', '),
        gradePosition: student.gradePosition,
        studentId: student.id,
        bronze,
        silver,
        gold,
        total,
      }
    })
  }

  get missingStudentsCountValue() {
    const allStudents = this.args.students
    const dataStudents = this.args.data
    return differenceWith(allStudents, dataStudents ?? [], (s, ds) => s.id === String(ds.student_id)).length
  }

  get chartTooltip() {
    const tooltip = this.intl.t(`reporting.tooltips.awardsEarnedHistory.rex`)
    const { missingStudentsCountValue } = this
    if (missingStudentsCountValue) {
      const suffix = this.intl.t('reporting.missingStudentsCount', { missingStudentsCount: missingStudentsCountValue })
      return `${tooltip} ${suffix}`
    }
    return tooltip
  }

  transitionToStudentRoute = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.rex.student-summary', studentId)
  }

  <template>
    <div class="relative flex h-full w-full pb-0 shadow-md! print:block" ...attributes>
      <ChartInfoTooltip @tooltipContent={{this.chartTooltip}} />
      {{#let this.processor.transformedData this.processor.tableAction as |transformedData|}}
        <table
          data-test-history-table-awards-earned
          class="relative w-full border-collapse overflow-hidden rounded-[5px] text-sm"
        >
          <thead class="bg-oceany-blue-300!">
            <tr class="h-20 cursor-pointer divide-x divide-white px-[10px]">
              <th
                class="w-36 px-[10px] font-normal text-white"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByValue "firstName")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.firstName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="firstName" />
                </div>
              </th>
              <th
                class="w-36 px-[10px] font-normal text-white"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByValue "lastName")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.dashboardTables.lastName" htmlSafe=true}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="lastName" />
                </div>
              </th>
              {{#if @subco}}
                <th
                  class="w-[80px] px-[10px] font-normal text-white"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByValue "teacherName")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "reporting.dashboardTables.teacher"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="teacherName" />
                  </div>
                </th>
                <th
                  class="w-[80px] max-w-[100px] px-[10px] font-normal text-white"
                  role="button"
                  {{on "click" (fn this.processor.tableActions.sortByNumber "gradePosition")}}
                >
                  <div class="flex items-center justify-between">
                    {{t "grade.label"}}
                    <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gradePosition" />
                  </div>
                </th>
              {{/if}}
              <th
                class="w-[80px] max-w-[100px] px-[10px] font-bold text-white"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "total")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.awardsEarned.trophies.totalTrophiesEarned"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="total" />
                </div>
              </th>
              <th
                class="w-[80px] px-[10px] text-center font-bold text-white"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "bronze")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.awardsEarned.trophies.bronzeLabel"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="bronze" />
                </div>
              </th>
              <th
                class="w-[80px] px-[10px] text-center font-bold text-white"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "silver")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.awardsEarned.trophies.silverLabel"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="silver" />
                </div>
              </th>
              <th
                class="w-[80px] px-[10px] text-center font-bold text-white"
                role="button"
                {{on "click" (fn this.processor.tableActions.sortByNumber "gold")}}
              >
                <div class="flex items-center justify-between">
                  {{t "reporting.charts.awardsEarned.trophies.goldLabel"}}
                  <SortColumnIcon @sortingPriorityQueue={{this.sortingPriorityQueue}} @columnName="gold" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="divide-dusty-black-100 divide-y-2 print:divide-black">
            {{#each transformedData as |row index|}}
              {{! template-lint-disable no-invalid-interactive }}
              <tr
                data-test-history-awards-earned-student-row={{index}}
                class="cursor-pointer"
                {{on "click" (fn (optional this.transitionToStudentRoute) row.studentId)}}
              >
                <td class="text-oceany-blue-300 h-[70px] px-[15px]! text-left">
                  {{row.firstName}}
                </td>
                <td class="text-oceany-blue-300 h-[70px] px-[15px]! text-left">
                  {{row.lastName}}
                </td>
                {{#if @subco}}
                  <td class="h-[70px] px-[15px] text-left">
                    {{row.teacherName}}
                  </td>
                  <td class="h-[70px] px-[15px] text-left">
                    {{gradeName row.gradePosition}}
                  </td>
                {{/if}}
                <td data-test-total class="h-[70px] px-[15px] text-center">
                  {{formatValue row.total}}
                </td>
                {{! Award medals }}
                <td class="bg-dusty-black-50 h-[70px] px-[15px]">
                  <div
                    class="trophy-bronze flex h-[52px] min-w-[52px] items-center justify-center bg-contain bg-center bg-no-repeat text-center font-bold print:bg-none
                      {{if (eq row.bronze 0) 'text-transparent opacity-40' 'text-black'}}
                      {{if (eq row.bronze null) 'text-black opacity-40'}}"
                  >
                    {{formatValue row.bronze}}
                  </div>
                </td>
                <td class="bg-dusty-black-50 h-[70px] px-[15px]">
                  <div
                    class="trophy-silver flex h-[52px] min-w-[52px] items-center justify-center bg-contain bg-center bg-no-repeat text-center font-bold print:bg-none
                      {{if (eq row.silver 0) 'text-transparent opacity-40' 'text-black'}}
                      {{if (eq row.silver null) 'text-black opacity-40'}}"
                  >
                    {{formatValue row.silver}}
                  </div>
                </td>
                <td class="bg-dusty-black-50 h-[70px] px-[15px]">
                  <div
                    class="trophy-gold flex h-[52px] min-w-[52px] items-center justify-center bg-contain bg-center bg-no-repeat text-center font-bold print:bg-none
                      {{if (eq row.gold 0) 'text-transparent opacity-40' 'text-black'}}
                      {{if (eq row.gold null) 'text-black opacity-40'}}"
                  >
                    {{formatValue row.gold}}
                  </div>
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/let}}
    </div>
  </template>
}

export default TrophiesHistory
